import React from "react";
import CustomModal from "../../../../components/Modal/CustomModal";
import CustomCard from "../../../../components/CustomCard";
import CustomAddChipSearch from "../../../../components/CustomAddChipSearch";
import { CustomAddChip } from "../../../../components/CustomAddChip";
import { CustomSelect } from "../../../../components/CustomSelect";
import { HeaderiEntry, PlusIconsOptions, SettingsIcon } from "../../../../components/buttons/IconButtons";
import AddPanelModalApp from "../Functionality/AddPanelModalApp";

/**
 * @Author      Karen Yunuen Pozos Arias
 * @JobTitle    Full Stack
 * @Email       kpozos@ibl.mx
 *
 * Adds a panel to the active layout with its type and source. When it receives a panel it configures
 * that panel instead; an unconfigured panel becomes active once confirmed.
 */
const AddPanelModal = ({ show, panel, onHide, onConfirm }) => {
    const {
        t,
        item,
        emptyFields,
        panelType,
        selectedSources,
        isUserPanel,
        isSearchPanel,
        userTypeId,
        userTypeIdsSearch,
        sanitizeSearch,
        panelTypeOptions,
        userTypeOptions,
        sourceOptions,
        handleChangeType,
        handleChangeUserType,
        handleChangeSources,
        handleConfirm
    } = AddPanelModalApp({ panel, onConfirm });

    return (
        <CustomModal
            show={show}
            onHide={onHide}
            size='md'
            onConfirm={handleConfirm}
            topLeftHeader={
                <div className='d-flex align-items-center gap-2 pb-2'>
                    {panel
                        ? <SettingsIcon className='btn-icon-svg-32px icon-color-text-950' />
                        : <PlusIconsOptions className='btn-icon-svg-32px icon-color-text-950' />
                    }
                    <p className='fs-24px ibl-font-w-600 font-color-text-950'>
                        {t(panel ? 'configure_panel' : 'add_panel')}
                    </p>
                </div>
            }
            classNameBody='pt-0'
        >
            <CustomCard classCard='card-border-8px' classContent='p-0'>
                <HeaderiEntry className='w-100 h-100' />
                <div className='d-flex flex-column gap-4 p-3 pt-4'>
                    <CustomSelect
                        id='slt_apm_panel_type'
                        name='typeId'
                        value={item.typeId}
                        keyName='id'
                        list={panelTypeOptions}
                        trans='panel_type'
                        onChange={handleChangeType}
                        emptyFields={emptyFields}
                        required={true}
                        showNoSelectionOption={false}
                    />

                    {panelType &&
                        <>
                            <p className='fs-14px ibl-font-w-700 text-uppercase text-center font-color-text-950'>
                                {t(panelType.SECTION)}
                            </p>

                            {isUserPanel &&
                                <CustomSelect
                                    id='slt_apm_user_type'
                                    name='userTypeId'
                                    value={userTypeId}
                                    keyName='id'
                                    list={userTypeOptions}
                                    trans='user_type'
                                    onChange={handleChangeUserType}
                                    emptyFields={emptyFields}
                                    required={true}
                                    showNoSelectionOption={false}
                                />
                            }

                            {/* The zones, controllers and users are searched in the backend with the simple search of their model */}
                            {isSearchPanel &&
                                <div>
                                    <CustomAddChipSearch
                                        key={panelType.ID}
                                        autocompleteId={`slt_apm_source_${panelType.ID}`}
                                        buttonId={`btn_apm_source_${panelType.ID}`}
                                        label={panelType.FIELD}
                                        selectedList={selectedSources}
                                        setSelectedList={handleChangeSources}
                                        model={panelType.SEARCH.MODEL}
                                        userTypeIdsSearch={userTypeIdsSearch}
                                        disabled={isUserPanel && !userTypeId}
                                        required={!panelType.SOURCE_OPTIONAL}
                                        emptyFields={emptyFields}
                                        reset={false}
                                        hideButton={true}
                                        keyId={panelType.SEARCH.KEY_ID}
                                        maxLength={1}
                                        showRenderOption={true}
                                        showSubSection={!!panelType.SEARCH.SHOW_SUB_SECTION}
                                        completeWidth={true}
                                        sanitizeDataList={sanitizeSearch}
                                        Icon={panelType.CARD_ICON}
                                    />
                                </div>
                            }

                            {/* The event types are a short fixed list that comes with the screen */}
                            {!isSearchPanel &&
                                <div>
                                    <CustomAddChip
                                        autocompleteId={`slt_apm_source_${panelType.ID}`}
                                        buttonId={`btn_apm_source_${panelType.ID}`}
                                        label={panelType.FIELD}
                                        data={sourceOptions}
                                        selectedList={selectedSources}
                                        setSelectedList={handleChangeSources}
                                        required={true}
                                        emptyFields={emptyFields}
                                        hideButton={true}
                                        maxLength={1}
                                        showRenderOption={true}
                                        completeWidth={true}
                                        chipIcon={panelType.CARD_ICON}
                                    />
                                </div>
                            }
                        </>
                    }
                </div>
            </CustomCard>
        </CustomModal>
    );
};

export default AddPanelModal;
